import { isIpLiteralTenantHostname, isReservedAptTenantSubdomain } from '../tenant/hostUtils.js';
import { buildStudioProductHostname, isStudioProductHostname } from './studioFlags.js';

const APT_ROOT_HOSTNAME = 'americanpolicetraining.com';
const LOCALHOST_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1']);

export function normalizeStudioHostname(input: string): string {
  const trimmed = input.trim().toLowerCase();
  if (!trimmed) {
    return '';
  }

  const withoutScheme = trimmed.replace(/^[a-z]+:\/\//, '');
  const hostnameWithPath = withoutScheme.split('/')[0] || '';
  const hostname = hostnameWithPath.split('@').pop() || '';
  return hostname.replace(/:\d+$/, '').replace(/\.+$/, '');
}

/**
 * Public hostname for coordinator dashboard links / status.
 * Prefer primary domain column → domain rows → studio_url → product subdomain from slug.
 */
export function resolveStudioPublicHostname(site: {
  primary_domain?: string | null;
  studio_domains?: Array<{ hostname?: string | null; is_primary?: boolean | null }> | null;
  studio_url?: string | null;
  slug?: string | null;
}): string | null {
  const fromColumn = site.primary_domain ? normalizeStudioHostname(String(site.primary_domain)) : '';
  if (fromColumn) return fromColumn;

  const domains = Array.isArray(site.studio_domains) ? site.studio_domains : [];
  const primary = domains.find((row) => row?.is_primary && row?.hostname);
  if (primary?.hostname) {
    const host = normalizeStudioHostname(String(primary.hostname));
    if (host) return host;
  }
  const first = domains.find((row) => row?.hostname);
  if (first?.hostname) {
    const host = normalizeStudioHostname(String(first.hostname));
    if (host) return host;
  }

  const studioUrl = site.studio_url ? String(site.studio_url).trim() : '';
  if (studioUrl) {
    const host = normalizeStudioHostname(studioUrl);
    if (host) return host;
  }

  const slug = site.slug ? String(site.slug).trim() : '';
  if (slug) return buildStudioProductHostname(slug);
  return null;
}

/** Hostname for live public traffic only — draft/preview tenants are not host-routable. */
export function resolveStudioLivePublicHostname(site: {
  status?: string | null;
  primary_domain?: string | null;
  studio_domains?: Array<{
    hostname?: string | null;
    is_primary?: boolean | null;
    is_verified?: boolean | null;
  }> | null;
  studio_url?: string | null;
  slug?: string | null;
}): string | null {
  if (String(site.status ?? '').trim().toLowerCase() !== 'active') {
    return null;
  }

  // Never advertise unverified BYOD hosts as the live public URL.
  const domains = Array.isArray(site.studio_domains) ? site.studio_domains : [];
  const verifiedPrimary = domains.find(
    (row) => row?.is_primary && row?.is_verified && row?.hostname,
  );
  if (verifiedPrimary?.hostname) {
    const host = normalizeStudioHostname(String(verifiedPrimary.hostname));
    if (host) return host;
  }
  const verifiedAny = domains.find((row) => row?.is_verified && row?.hostname);
  if (verifiedAny?.hostname) {
    const host = normalizeStudioHostname(String(verifiedAny.hostname));
    if (host) return host;
  }

  const fromColumn = site.primary_domain ? normalizeStudioHostname(String(site.primary_domain)) : '';
  // primary_domain column is only trusted when it matches a verified domain row, or when there are no domain rows
  if (fromColumn) {
    const columnVerified = domains.some(
      (row) =>
        row?.is_verified &&
        normalizeStudioHostname(String(row.hostname ?? '')) === fromColumn,
    );
    if (columnVerified || domains.length === 0) {
      return fromColumn;
    }
  }

  const studioUrl = site.studio_url ? String(site.studio_url).trim() : '';
  if (studioUrl) {
    const host = normalizeStudioHostname(studioUrl);
    if (host && (isStudioProductHostname(host) || domains.length === 0)) return host;
  }

  const slug = site.slug ? String(site.slug).trim() : '';
  if (slug) return buildStudioProductHostname(slug);
  return null;
}

export function isStudioLocalDevHostname(input: string): boolean {
  const hostname = normalizeStudioHostname(input);
  return LOCALHOST_HOSTNAMES.has(hostname);
}

export function isLikelyStudioPublicHostname(input: string): boolean {
  const hostname = normalizeStudioHostname(input);
  if (!hostname || LOCALHOST_HOSTNAMES.has(hostname) || isIpLiteralTenantHostname(hostname)) {
    return false;
  }

  if (isStudioProductHostname(hostname)) {
    return true;
  }

  if (hostname === APT_ROOT_HOSTNAME || hostname === `www.${APT_ROOT_HOSTNAME}`) {
    return false;
  }

  if (!hostname.endsWith(`.${APT_ROOT_HOSTNAME}`)) {
    return true;
  }

  const subdomain = hostname.slice(0, -(`.${APT_ROOT_HOSTNAME}`).length);
  const firstLabel = subdomain.split('.')[0] || subdomain;
  return !isReservedAptTenantSubdomain(subdomain) && !isReservedAptTenantSubdomain(firstLabel);
}

/** Mount StudioPublicRouter on custom studio hosts or local Vite dev preview. */
export function shouldMountStudioPublicRouter(options: {
  hostname: string;
  viteStudioEnabled: boolean;
  devPreviewEnabled: boolean;
}): boolean {
  const { hostname, viteStudioEnabled, devPreviewEnabled } = options;
  if (!viteStudioEnabled) {
    return false;
  }
  if (isLikelyStudioPublicHostname(hostname)) {
    return true;
  }
  return devPreviewEnabled && isStudioLocalDevHostname(hostname);
}

export function appendStudioSiteIdQuery(url: string, siteId?: number | string | null): string {
  if (siteId == null || siteId === '') {
    return url;
  }
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}siteId=${encodeURIComponent(String(siteId))}`;
}
